// src/pages/EditInvoicePage.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  collection, query, getDocs, orderBy, Timestamp,
  doc, getDoc, updateDoc, serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import styles from '../styles/CreateInvoicePage.module.css';
import { FiPlus, FiTrash2 } from 'react-icons/fi';

// ILS currency formatter
const currencyILS = (n) =>
  new Intl.NumberFormat('en-IL', { style: 'currency', currency: 'ILS' }).format(Number(n) || 0);

// Timestamp/Date -> 'YYYY-MM-DD' for date inputs
const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';
  try {
    const date = (dateValue instanceof Timestamp) ? dateValue.toDate() : new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  } catch {
    return '';
  }
};

const EditInvoicePage = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const isMounted = useRef(true);

  // Form State
  const [clients, setClients] = useState([]);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [clientId, setClientId] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [lineItems, setLineItems] = useState([{ description: '', quantity: 1, unitPrice: 0 }]);
  const [notes, setNotes] = useState('');
  const [taxRate, setTaxRate] = useState(0);

  // Helper State
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Load clients + invoice
  useEffect(() => {
    const fetchData = async () => {
      if (!isMounted.current || !invoiceId) return;
      try {
        const clientsSnapshot = await getDocs(query(collection(db, 'clients'), orderBy('name')));
        const clientsList = clientsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        if (isMounted.current) setClients(clientsList);

        const invoiceRef = doc(db, 'invoices', invoiceId);
        const invoiceSnap = await getDoc(invoiceRef);
        if (!invoiceSnap.exists()) {
          if (isMounted.current) setError('Invoice not found.');
        } else {
          const data = invoiceSnap.data();
          if (isMounted.current) {
            setClientId(data.clientId || '');
            setInvoiceNumber(data.invoiceNumber || '');
            setIssueDate(formatDateForInput(data.issueDate));
            setDueDate(formatDateForInput(data.dueDate));
            setLineItems(data.lineItems || [{ description: '', quantity: 1, unitPrice: 0 }]);
            setNotes(data.notes || '');
            setTaxRate(data.taxRate || 0);
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        if (isMounted.current) setError('Failed to load data. Please refresh.');
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };
    fetchData();
  }, [invoiceId]);

  const handleLineItemChange = useCallback((index, field, value) => {
    setLineItems(prev => {
      const next = [...prev];
      next[index][field] = value;
      return next;
    });
  }, []);

  const addLineItem = useCallback(() => {
    setLineItems(prev => [...prev, { description: '', quantity: 1, unitPrice: 0 }]);
  }, []);

  const removeLineItem = useCallback((index) => {
    setLineItems(prev => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  }, []);

  const totals = useMemo(() => {
    const subtotal = lineItems.reduce((acc, item) => {
      const q = parseFloat(item.quantity) || 0;
      const p = parseFloat(item.unitPrice) || 0;
      return acc + q * p;
    }, 0);
    const taxAmount = subtotal * (parseFloat(taxRate) / 100 || 0);
    const totalAmount = subtotal + taxAmount;
    return { subtotal, taxAmount, totalAmount };
  }, [lineItems, taxRate]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!clientId) { setError('Please select a client.'); return; }
    if (!issueDate || !dueDate) { setError('Both issue date and due date are required.'); return; }
    if (new Date(dueDate) < new Date(issueDate)) { setError('Due date cannot be before the issue date.'); return; }
    if (lineItems.some(it => !it.description.trim() || it.quantity <= 0 || it.unitPrice < 0)) {
      setError('All line items must have a valid description, positive quantity, and non-negative price.');
      return;
    }

    setIsProcessing(true);

    const selectedClient = clients.find(c => c.id === clientId);
    const finalLineItems = lineItems.map(it => ({
      ...it,
      total: (parseFloat(it.quantity) || 0) * (parseFloat(it.unitPrice) || 0),
    }));

    const updatedInvoiceData = {
      clientId,
      clientName: selectedClient?.name || 'Unknown Client',
      issueDate: Timestamp.fromDate(new Date(`${issueDate}T00:00:00`)),
      dueDate: Timestamp.fromDate(new Date(`${dueDate}T00:00:00`)),
      lineItems: finalLineItems,
      subtotal: totals.subtotal,
      taxRate: parseFloat(taxRate) || 0,
      taxAmount: totals.taxAmount,
      totalAmount: totals.totalAmount,
      notes: notes.trim(),
      updatedAt: serverTimestamp(),
    };

    try {
      await updateDoc(doc(db, 'invoices', invoiceId), updatedInvoiceData);
      if (isMounted.current) {
        setSuccessMessage('Invoice updated successfully!');
        setTimeout(() => navigate(`/invoices/${invoiceId}`), 1500);
      }
    } catch (err) {
      console.error('Error updating invoice:', err);
      if (isMounted.current) setError('Failed to update invoice. Please try again.');
    } finally {
      if (isMounted.current) setIsProcessing(false);
    }
  };

  if (loading) {
    return <div className={styles.loadingIndicator}>Loading Invoice for Editing...</div>;
  }

  return (
    <div className={styles.pageContainer}>
      <h1 className={styles.pageTitle}>Edit Invoice {invoiceNumber}</h1>

      <form onSubmit={handleUpdate} className={styles.invoiceForm}>
        <div className={styles.topSection}>
          <div className={styles.formGroup}>
            <label htmlFor="client">Client *</label>
            <select
              id="client"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              required
              disabled={isProcessing}
            >
              <option value="">-- Select a Client --</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="invoiceNumber">Invoice #</label>
            <input
              type="text"
              id="invoiceNumber"
              value={invoiceNumber}
              readOnly
              disabled
              className={styles.readOnlyInput}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="issueDate">Issue Date *</label>
            <input
              type="date"
              id="issueDate"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              required
              disabled={isProcessing}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="dueDate">Due Date *</label>
            <input
              type="date"
              id="dueDate"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              required
              disabled={isProcessing}
            />
          </div>
        </div>

        <h3 className={styles.sectionTitle}>Line Items</h3>
        <div className={styles.lineItemsContainer}>
          <div className={styles.lineItemsHeader}>
            <div className={styles.descriptionHeader}>Description</div>
            <div>Quantity</div>
            <div>Unit Price</div>
            <div>Total</div>
            <div>Action</div>
          </div>

          {lineItems.map((item, index) => (
            <div key={index} className={styles.lineItem}>
              <input
                type="text"
                placeholder="Service or Product Description"
                value={item.description}
                onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                className={styles.descriptionInput}
                required
                disabled={isProcessing}
              />
              <input
                type="number"
                min="0"
                step="any"
                value={item.quantity}
                onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                required
                disabled={isProcessing}
              />
              <input
                type="number"
                min="0"
                step="any"
                placeholder="0.00"
                value={item.unitPrice}
                onChange={(e) => handleLineItemChange(index, 'unitPrice', e.target.value)}
                required
                disabled={isProcessing}
              />
              <div className={styles.lineTotal}>
                {currencyILS((parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0))}
              </div>
              <button
                type="button"
                onClick={() => removeLineItem(index)}
                className={styles.removeButton}
                disabled={isProcessing || lineItems.length <= 1}
              >
                <FiTrash2 />
              </button>
            </div>
          ))}

          <button type="button" onClick={addLineItem} className={styles.addButton} disabled={isProcessing}>
            <FiPlus /> Add Line Item
          </button>
        </div>

        <div className={styles.bottomSection}>
          <div className={styles.notesSection}>
            <label htmlFor="notes">Notes / Payment Instructions</label>
            <textarea
              id="notes"
              rows="4"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Thank you for your business. Please pay within 30 days."
              disabled={isProcessing}
            />
          </div>

          <div className={styles.summarySection}>
            <div className={styles.summaryRow}>
              <span>Subtotal</span>
              <span>{currencyILS(totals?.subtotal || 0)}</span>
            </div>
            <div className={styles.summaryRow}>
              <label htmlFor="taxRate">Tax (%)</label>
              <input
                type="number"
                id="taxRate"
                min="0"
                step="any"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                placeholder="e.g., 17"
                disabled={isProcessing}
              />
            </div>
            <div className={styles.summaryRow}>
              <span>Tax Amount</span>
              <span>{currencyILS(totals?.taxAmount || 0)}</span>
            </div>
            <div className={`${styles.summaryRow} ${styles.totalRow}`}>
              <strong>Total Amount</strong>
              <strong>{currencyILS(totals?.totalAmount || 0)}</strong>
            </div>
          </div>
        </div>

        <div className={styles.formFooter}>
          {error && <p className={styles.errorMessage}>{error}</p>}
          {successMessage && <p className={styles.successMessage}>{successMessage}</p>}
          <button
            type="button"
            onClick={() => navigate(`/invoices/${invoiceId}`)}
            className={styles.cancelButton}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button type="submit" className={styles.submitButton} disabled={isProcessing}>
            {isProcessing ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditInvoicePage;
